import type {
  CampaignInitiateRequest,
  CampaignWebhookCreateRequest,
} from '../../services/campaignService';

export const DEFAULT_REENGAGEMENT_DELAY_DAYS = 4;
export const DEFAULT_NO_ANSWER_REENGAGEMENT_ATTEMPTS = 2;
export const DEFAULT_NO_ANSWER_REENGAGEMENT_INCREMENT_DAYS = 1;

type BuildInitiateCampaignRequestParams = {
  templateName: string;
  phoneNumber: string;
  customerName: string;
  skipIdentityValidation?: boolean;
  resetConversationMemory?: boolean;
  headerMediaType?: 'image' | 'video' | null;
  headerImageMediaId?: string | null;
  headerImageUrl?: string | null;
  scheduleReengagement?: boolean;
  reengagementDelayBusinessDays?: number;
  noAnswerReengagementAttempts?: number;
  noAnswerReengagementIncrementBusinessDays?: number;
  projectInterest?: string | null;
};

const normalizePhoneNumber = (rawPhone: string): string =>
  (rawPhone || '').replace(/\D+/g, '');

export const buildInitiateCampaignRequest = ({
  templateName,
  phoneNumber,
  customerName,
  skipIdentityValidation = false,
  resetConversationMemory = false,
  headerMediaType = null,
  headerImageMediaId = null,
  headerImageUrl = null,
  scheduleReengagement = true,
  reengagementDelayBusinessDays = DEFAULT_REENGAGEMENT_DELAY_DAYS,
  noAnswerReengagementAttempts = DEFAULT_NO_ANSWER_REENGAGEMENT_ATTEMPTS,
  noAnswerReengagementIncrementBusinessDays = DEFAULT_NO_ANSWER_REENGAGEMENT_INCREMENT_DAYS,
  projectInterest = null,
}: BuildInitiateCampaignRequestParams): CampaignInitiateRequest => ({
  template_name: templateName.trim(),
  phone_number: normalizePhoneNumber(phoneNumber),
  customer_name: customerName.trim(),
  skip_identity_validation: skipIdentityValidation,
  reset_conversation_memory: resetConversationMemory,
  schedule_reengagement: scheduleReengagement,
  ...(scheduleReengagement ? {
    reengagement_delay_business_days: reengagementDelayBusinessDays,
    no_answer_reengagement_attempts: noAnswerReengagementAttempts,
    no_answer_reengagement_increment_business_days: noAnswerReengagementIncrementBusinessDays,
  } : {}),
  ...(projectInterest?.trim() ? { project_interest: projectInterest.trim() } : {}),
  ...(headerMediaType ? { header_media_type: headerMediaType } : {}),
  ...(headerImageMediaId ? { header_media_id: headerImageMediaId } : {}),
  ...(headerImageUrl ? { header_media_url: headerImageUrl } : {}),
  ...(headerMediaType === 'image' && headerImageMediaId ? { header_image_media_id: headerImageMediaId } : {}),
  ...(headerMediaType === 'image' && headerImageUrl ? { header_image_url: headerImageUrl } : {}),
});

export const parseRuntimeVariableNamesInput = (rawValue: string): string[] => {
  const values = (rawValue || '')
    .split(/[\n,]/g)
    .map((item) => item.trim())
    .filter(Boolean);
  return [...new Map(values.map((value) => [value.toLowerCase(), value])).values()];
};

type BuildWebhookCampaignRequestParams = {
  campaignName: string;
  voiceModelSelection?: string | null;
  templateName: string;
  skipIdentityValidation?: boolean;
  runtimeVariableNamesInput?: string;
  headerMediaType?: 'image' | 'video' | null;
  headerImageMediaId?: string | null;
  headerImageUrl?: string | null;
  hubspotSignatureSecret?: string;
  scheduleReengagement?: boolean;
  reengagementDelayBusinessDays?: number;
  noAnswerReengagementAttempts?: number;
  noAnswerReengagementIncrementBusinessDays?: number;
  projectInterest?: string | null;
};

export const buildWebhookCampaignRequest = ({
  campaignName,
  voiceModelSelection = null,
  templateName,
  skipIdentityValidation = false,
  runtimeVariableNamesInput = '',
  headerMediaType = null,
  headerImageMediaId = null,
  headerImageUrl = null,
  hubspotSignatureSecret = '',
  scheduleReengagement = true,
  reengagementDelayBusinessDays = DEFAULT_REENGAGEMENT_DELAY_DAYS,
  noAnswerReengagementAttempts = DEFAULT_NO_ANSWER_REENGAGEMENT_ATTEMPTS,
  noAnswerReengagementIncrementBusinessDays = DEFAULT_NO_ANSWER_REENGAGEMENT_INCREMENT_DAYS,
  projectInterest = null,
}: BuildWebhookCampaignRequestParams): CampaignWebhookCreateRequest => ({
  campaign_name: campaignName.trim(),
  ...(voiceModelSelection?.trim() ? { voice_model_selection: voiceModelSelection.trim() } : {}),
  template_name: templateName.trim(),
  skip_identity_validation: skipIdentityValidation,
  runtime_variable_names: parseRuntimeVariableNamesInput(runtimeVariableNamesInput),
  schedule_reengagement: scheduleReengagement,
  ...(scheduleReengagement ? {
    reengagement_delay_business_days: reengagementDelayBusinessDays,
    no_answer_reengagement_attempts: noAnswerReengagementAttempts,
    no_answer_reengagement_increment_business_days: noAnswerReengagementIncrementBusinessDays,
  } : {}),
  ...(projectInterest?.trim() ? { project_interest: projectInterest.trim() } : {}),
  ...(headerMediaType ? { header_media_type: headerMediaType } : {}),
  ...(headerImageMediaId ? { header_media_id: headerImageMediaId } : {}),
  ...(headerImageUrl ? { header_media_url: headerImageUrl } : {}),
  ...(headerMediaType === 'image' && headerImageMediaId ? { header_image_media_id: headerImageMediaId } : {}),
  ...(headerMediaType === 'image' && headerImageUrl ? { header_image_url: headerImageUrl } : {}),
  ...(hubspotSignatureSecret.trim() ? { hubspot_signature_secret: hubspotSignatureSecret.trim() } : {}),
});
